"use client";

import type { EducatorVideoTranscript } from "@/components/educators/data/video-transcripts";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ClosedCaptioning, X } from "@phosphor-icons/react";
import { useId, useState } from "react";

type VideoTranscriptDialogProps = {
  transcript: EducatorVideoTranscript;
  /** Used in accessible names, e.g. video title. */
  videoTitle: string;
  /** Test id prefix for the trigger. */
  testId?: string;
  /** Optional class override for the subtle trigger button. */
  triggerClassName?: string;
};

/**
 * Subtle CC icon + modal. Full transcript also stays in an always-rendered
 * sr-only block so crawlers can index it without opening the dialog.
 */
export function VideoTranscriptDialog({
  transcript,
  videoTitle,
  testId = "video-transcript",
  triggerClassName,
}: VideoTranscriptDialogProps) {
  const [open, setOpen] = useState(false);
  const seoHeadingId = useId();

  return (
    <>
      {/* Crawlable transcript — always in the HTML, not only when the modal is open. */}
      <section className="sr-only" aria-labelledby={seoHeadingId} data-testid={`${testId}-seo`}>
        <h3 id={seoHeadingId}>
          {videoTitle} transcript — {transcript.speaker}
        </h3>
        {transcript.paragraphs.map((paragraph, index) => (
          <p key={`${index}-${paragraph.slice(0, 24)}`}>{paragraph}</p>
        ))}
      </section>

      <button
        type="button"
        data-testid={`${testId}-trigger`}
        aria-label="Read transcript"
        onClick={(event) => {
          event.stopPropagation();
          setOpen(true);
        }}
        className={
          triggerClassName ??
          "text-white/40 hover:text-white/75 focus-visible:text-white p-1 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50"
        }
      >
        <ClosedCaptioning
          className="h-4 w-4 md:h-[18px] md:w-[18px]"
          weight="bold"
          aria-hidden="true"
        />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-lg max-h-[85vh] overflow-hidden flex flex-col gap-0 p-0 sm:rounded-2xl border-2 border-black"
          data-testid={`${testId}-dialog`}
        >
          <DialogHeader className="px-6 pt-6 pb-3 pr-12 text-left">
            <DialogTitle className="charlie-kingdom-font text-brand-purple-deep text-2xl leading-tight">
              Video transcript
            </DialogTitle>
            <DialogDescription className="text-ink-slate font-semibold">
              {transcript.speaker}
            </DialogDescription>
          </DialogHeader>

          <div className="px-6 pb-2 overflow-y-auto flex-1 space-y-4">
            {transcript.paragraphs.map((paragraph, index) => (
              <p
                key={`${index}-${paragraph.slice(0, 24)}`}
                className="text-ink-slate text-sm md:text-base leading-relaxed"
              >
                {paragraph}
              </p>
            ))}
          </div>

          <DialogFooter className="px-6 py-4 border-t border-black/10">
            <DialogClose asChild>
              <button
                type="button"
                className="inline-flex items-center justify-center rounded-full border-2 border-black bg-cta-yellow px-5 py-2 charlie-kingdom-font text-base text-black shadow-cta-yellow hover:bg-cta-yellow-hover focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-black/20"
              >
                Close
              </button>
            </DialogClose>
          </DialogFooter>

          <DialogClose
            className="absolute right-4 top-4 rounded-full p-1 text-ink-slate/70 hover:text-ink-slate focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple"
            aria-label="Close transcript"
          >
            <X className="h-5 w-5" weight="bold" aria-hidden="true" />
          </DialogClose>
        </DialogContent>
      </Dialog>
    </>
  );
}
